import $ from 'jquery';
import { loadPartial } from '../../remote/partial-content.js';
import { isElementInViewport } from '../../util/dom.js';

/**
 * Process special actions bound on form or form elements
 *
 * @param {JQuery<HTMLElement>|HTMLElement} container
 *
 * @returns {void}
 */
mpro.handleSectionReload = (container) => {

	/** @type {JQuery<HTMLElement>} */
	// @ts-ignore
	const $container = (container instanceof $) ? container : $(container);

	// Add sections
	const $sections = $container.find('[data-section-id]').add($container.filter('[data-section-id]'));

	$sections.on('mp-section-reload.section-reload-handler', function(/** @type {JQuery.TriggeredEvent} */e, url, data) {
		e.stopPropagation();
		e.stopImmediatePropagation();

		// @ts-ignore
		const $elem = $(e.currentTarget);
		/** @type {{ [key: string]: any; sectionId: string; }} */ // @ts-ignore
		const elemData = $elem.data();
		const { sectionId, sectionShowLoader: showLoader } = elemData;

		if (!sectionId) {
			console.warn('Cannot reload section for element (%o) - missing section id', this);
		}

		// Reload the section with delay (also filter out undefined values)
		mpro.shop.section.reloadDelayed([sectionId], Object.fromEntries(Object.entries({ url, data, showLoader }).filter(([_, v]) => v !== undefined)));
	});
};

/**
 * @param {JQuery<HTMLElement>|HTMLElement} container
 */
mpro.handleFragmentLoad = (container) => {

	/** @type {JQuery<HTMLElement>} */
	// @ts-ignore
	const $container = (container instanceof $) ? container : $(container);
	const triggerSelector = '[data-load-fragment]';

	// Get all triggers
	const $triggers = $container.find(triggerSelector).add($container.filter(triggerSelector));

	$triggers.on('click.loadFragment', function(e) {

		// Disable other actions
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation();

		mpro.loadFragment(this);
	});

	// Get triggers that have auto-click when in viewport
	const $triggersInViewport = $triggers.filter('[data-load-fragment-in-view]');

	$triggersInViewport.each(function() {
		const $trigger = $(this);
		const threshold = $trigger.data('load-fragment-in-view') || 0;
		const checkInViewport = () => {
			if (isElementInViewport(this, threshold)) {
				this.click();
				window.removeEventListener('scroll', checkInViewport);
			}
		};
		window.addEventListener('scroll', checkInViewport, { passive: true });
		checkInViewport();
	});
};

/**
 * @param {HTMLElement} triggerElement
 */
mpro.loadFragment = async (triggerElement) => {

	const $trigger = $(triggerElement);
	const data = $trigger.data();
	const allowRestore = ('loadFragmentAllowRestore' in data) && (data.loadFragmentAllowRestore === '' || data.loadFragmentAllowRestore === true);
	const append = ('loadFragmentAppend' in data) && (data.loadFragmentAppend === '' || data.loadFragmentAppend === true);
	const $context = data.loadFragmentContainer ? $(data.loadFragmentContainer).first() : $trigger;
	const buttonContainerSelector = data.loadFragmentButtonContainer;
	const $originalButtonContainer = buttonContainerSelector ? $trigger.closest(buttonContainerSelector) : $();

	// Show loader
	mpro.addButtonLoading($trigger);

	// Load the partial content in place
	const partial = await loadPartial($context, {
		type: 'fragment',
		url: data.loadFragmentUrl || $trigger.attr('href'),
		method: 'GET',
		loader: false,
		animation: true,
		replaceContainer: ((append === false) && ($context[0] === $trigger[0])) ? true : false,
		append,
		contentSelector: data.loadFragmentSelector,
		strictContentSelector: true,
		restoreOriginal: (allowRestore === true),
	});

	// Got redirect location
	if (partial.redirect) {
		// Close everything
		mpro.closeAllOverlays();
		// TODO: add option to decide what to do with locations (do redirect or load the content form that location)
		return void mpro.setLocation(partial.redirect/* , { replace: true } */);
	}
	// Got success/error message
	else if (partial.message) {
		// Display the message in a toast
		mpro.toast(partial.message.type || 'info', partial.message.content);
	}
	// Re-parent the trigger
	else if (partial.content) {

		// Replace the whole button container
		if (buttonContainerSelector && $originalButtonContainer.length > 0) {
			let $buttonContainer = partial.content.filter(buttonContainerSelector).first();
			if ($buttonContainer.length === 0) {
				$buttonContainer = partial.content.find(buttonContainerSelector).first();
			}

			// Use the new button container
			if ($buttonContainer.length > 0) {
				$originalButtonContainer.replaceWith($buttonContainer);
			}
			// No buttons container found - remove it
			else {
				$originalButtonContainer.remove();
			}
		}
	}

	// Remove the loader
	mpro.removeButtonLoading($trigger);

	// Remove the trigger (in most cases this trigger is already removed by the content that is replaced with)
	// And we must use detach to preserve the event listeners
	if (allowRestore === true) {

		let $next;
		let $parent;
		const $prev = $trigger.prev();
		if ($prev.length === 0) {
			$next = $trigger.next();
			if ($next.length === 0) {
				$parent = $trigger.parent();
			}
		}

		$trigger.detach();

		if (partial.content) {
			partial.content.find('[data-restore-original]').one('click', (e) => {
				if ($prev && $prev.length > 0) {
					$trigger.insertAfter($prev);
				}
				else if ($next && $next.length > 0) {
					$trigger.insertBefore($next);
				}
				else if ($parent && $parent.length > 0) {
					$parent.append($trigger);
				}
			});
		}
	}
	else {
		$trigger.remove();
	}

	return partial;
};

// Add init function that is executed on every page/fragment render
mpro.addInitStatic(($container) => {

	// Section reload handler
	mpro.handleSectionReload($container);

	// Fragment load handler
	mpro.handleFragmentLoad($container);
});

export default mpro;
